import React from 'react';
import Icons from '../../icons';
import {
  ORDER_STATUSES_DISPLAY,
  ORDER_TYPES,
  STEP_TYPE,
  TIME_FORMAT,
  ORDER_PICKUP_STATUS_COMPLETED,
  ORDER_DELIVERY_STATUS_COMPLETED,
} from '../../constants';
import './StopClicked.scss';
import { useSelector } from 'react-redux';
import { selectAllOrders } from '../../modules/orders/stores/slice';
import moment from 'moment';
import { Tag } from 'antd';

export default function StopClicked({ item, solutions, onStepClick }) {
  const keyItems = Object.keys(solutions).find((key) =>
    solutions[key]?.some((step) => step.id === item.id && step.type.toLowerCase() === item.type.toLowerCase()),
  );

  const orders = useSelector(selectAllOrders);

  const items = solutions[keyItems] || [];
  const step = items?.find((i) => i.id === item.id && i.type.toLowerCase() === item.type.toLowerCase());
  if (!step) return null;

  const hdStepClick = (e, step) => {
    e.stopPropagation();
    e.preventDefault();
    onStepClick && onStepClick({ id: step.id, type: step.type, orderId: step.orderId, orderIds: step.orderIds });
  };

  const closeStep = () => {
    onStepClick && onStepClick({});
  };

  const completedTime = (stepData) => {
    // For delivery, use first orderId from orderIds array; for pickup, use first orderId
    const orderIds = stepData.orderIds || (stepData.orderId ? [stepData.orderId] : []);
    const orderId = orderIds[0];
    const type = stepData.type;
    const logs = stepData?.logs || [];
    const order = orders.find((o) => o.id === orderId);
    if (!order) return null;
    if (ORDER_PICKUP_STATUS_COMPLETED.includes(order?.pickupStatus) && type === STEP_TYPE.pickup) {
      const completedLogs = logs.filter((l) => ORDER_PICKUP_STATUS_COMPLETED.includes(l.status));
      const completedLog = completedLogs[completedLogs.length - 1];
      return completedLog?.createdAt;
    }
    if (ORDER_DELIVERY_STATUS_COMPLETED.includes(order?.status) && type === STEP_TYPE.delivery) {
      const completedLogs = logs.filter((l) => ORDER_DELIVERY_STATUS_COMPLETED.includes(l.status));
      const completedLog = completedLogs[completedLogs.length - 1];
      return completedLog?.createdAt;
    }
    return null;
  };

  const getPickupNames = (orderIds) => {
    const names = orderIds.map((orderId) => {
      const order = orders.find((o) => o.id === orderId);
      return order?.pickupName;
    });
    const uniqueNames = [...new Set(names)];
    return uniqueNames.map((name, index) => (
      <span key={index + name}>
        {name}
        {index < uniqueNames.length - 1 && <br />}
      </span>
    ));
  };

  const headerContent = (
    <div className="info-header">
      <Icons type="InfoWindowCar" className="info-icon" color={step.color} />
      <a
        className="info-id"
        style={{ color: step.color }}
        href={`${window.location.origin}/admin/orders/${step.orderCode}`}
        target="_blank"
        rel="noopener noreferrer"
      >
        {step.orderCode}
      </a>
      <a className="btn-close" onClick={closeStep}>
        x
      </a>
    </div>
  );

  const steps =
    step.type === STEP_TYPE.delivery
      ? items.filter((i) => {
          // For delivery, find all pickups that have any orderId matching any orderId in delivery's orderIds
          const deliveryOrderIds = step.orderIds || (step.orderId ? [step.orderId] : []);
          return i.type === STEP_TYPE.pickup && deliveryOrderIds.some((orderId) => i.orderIds?.includes(orderId));
        })
      : items.filter((i) => {
          // For pickup, find deliveries that have any orderId matching any orderId in pickup's orderIds
          const pickupOrderIds = step.orderIds || [];
          return i.type === STEP_TYPE.delivery && pickupOrderIds.some((orderId) => i.orderIds?.includes(orderId));
        });

  const infoWindow = (
    <div className="info-window">
      <div className="info-content">
        <div className="info-detail">
          <strong>Type</strong> <span>{step?.type}</span>
        </div>
        {step.type === STEP_TYPE.pickup && (
          <div className="info-detail">
            <strong>Pickup Name</strong> <span>{getPickupNames(step.orderIds)}</span>
          </div>
        )}
        {step.type === STEP_TYPE.delivery && (
          <div className="info-detail">
            <strong>Delivery Name</strong> <span>{step?.name}</span>
          </div>
        )}

        <div className="info-detail">
          <strong>Location</strong> <span>{step?.address}</span>
        </div>
        <div className="info-detail">
          <strong>Partner</strong> <span>{step?.partner || '-'}</span>
        </div>
        <div className="info-detail">
          <strong>Order Types</strong> <span>{step?.orderTypes?.join(', ')}</span>
        </div>
        <div className="info-detail">
          <strong>Ticket Names</strong> <span>{step?.ticketNames?.join(', ')}</span>
        </div>
        {step.type !== STEP_TYPE.pickup && (
          <div className="info-detail">
            <strong>Status</strong> <span>{ORDER_STATUSES_DISPLAY[step?.orderStatus] || step?.orderStatus}</span>
          </div>
        )}
        {step.type === STEP_TYPE.delivery && (
          <div className="info-detail">
            <strong>Packages</strong> <span>{step?.orderIds?.length || '-'}</span>
          </div>
        )}
        <div className="info-detail">
          <strong>Arrival Time</strong> <span>{step?.arrivalTime || '-'}</span>
        </div>
        <div className="info-detail">
          <strong>Finish Time</strong> <span>{step?.finishTime || '-'}</span>
        </div>
        {completedTime(step) && (
          <div className="info-detail">
            <strong>Completed Time</strong> <span>{moment(completedTime(step)).format(TIME_FORMAT)}</span>
          </div>
        )}
        {step?.orderType === ORDER_TYPES.SERVICE && (
          <div className="info-detail">
            <Tag color="orange">Service</Tag>
          </div>
        )}
        {step?.collectedItems?.length > 0 && (
          <div className="info-detail collected-items-section">
            <strong>Collected Items ({step.collectedItems.length})</strong>
            <div className="collected-items-list">
              {step.collectedItems.map((item) => (
                <div key={item.id} className="collected-item">
                  <span className="item-name">{item.partnerItemType?.name || item.description || 'Item'}</span>
                  {item.scannedCode && <span className="item-code">{item.scannedCode}</span>}
                  {item.quantity > 1 && <span className="item-qty">x{item.quantity}</span>}
                  {item.partner?.name && <span className="item-partner">{item.partner.name}</span>}
                </div>
              ))}
            </div>
          </div>
        )}
        {step?.unscheduledReason && (
          <div className="info-detail">
            <strong>Reason</strong>
            <span style={{ color: 'red' }}>{step?.unscheduledReason}</span>
          </div>
        )}
        <div className="info-detail">
          {step.type === STEP_TYPE.delivery && steps?.length > 0 && (
            <>
              {' '}
              <strong>Pickups: </strong>
              <div className="steps">
                {steps.map((step) => {
                  return (
                    <div className="step" key={step.id} onClick={(e) => hdStepClick(e, step)}>
                      <div className="pickup" style={{ border: `2px solid ${step.color}`, color: step.color }}>
                        {step?.index}
                      </div>
                      <b>{getPickupNames(step.orderIds)}</b>
                    </div>
                  );
                })}
              </div>
            </>
          )}
          {step.type === STEP_TYPE.pickup && (
            <>
              {' '}
              <strong>Deliveries: </strong>
              <div className="steps">
                {steps.map((step) => {
                  const stepKey =
                    step.orderIds?.length > 0 ? `${step.orderIds[0]}_${step.type}` : `${step.id}_${step.type}`;
                  return (
                    <div className="step" key={stepKey} onClick={(e) => hdStepClick(e, step)}>
                      <div className="delivery" style={{ border: `2px solid ${step.color}`, color: step.color }}>
                        {step?.index}
                      </div>
                      <b>{step.name}</b>
                      <a className="status">{ORDER_STATUSES_DISPLAY[step?.pickupStatus] || step?.pickupStatus}</a>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
  return (
    <div className="StopClicked">
      {headerContent}
      {infoWindow}
    </div>
  );
}
